import type { IContentComponent, IHTMLBlockItem } from "../../../models/Interfaces";

// Inline editing is limited to HTML and the content of individual HTML blocks.
export const inlineHtmlProperty = (component?: IContentComponent) => {
  const name = component?.BaseComponent.Name?.trim().toLowerCase();
  // The renderer calls the standalone HTML component BodyText internally.
  const blocks = name === "htmlblock" || name === "html blocks";
  if (!blocks && name !== "html" && name !== "bodytext") return undefined;
  if (!component) return undefined;
  return component.BaseComponent.Properties.find(property =>
    property.PropertyName?.toLowerCase() === (blocks ? "items" : "bodytext")
    && property.Type?.Name?.toLowerCase() === (blocks ? "htmlitems" : "bodytext")
    && property.UserEditable && !property.External
  );
};

export const withInlineHtml = (component: IContentComponent, propertyId: number, html: string): IContentComponent => ({
  ...component,
  BaseComponent: {
    ...component.BaseComponent,
    Properties: component.BaseComponent.Properties.map(property =>
      property.Id === propertyId ? { ...property, PublishedValue: html } : property
    )
  }
});

export const inlineHtmlBlocks = (value: string): (Omit<IHTMLBlockItem, "Id"> & { Id: number | string })[] => {
  try {
    const items: unknown = JSON.parse(value);
    return Array.isArray(items) && items.every(item => item && (typeof item.Id === "number" || typeof item.Id === "string")
      && typeof item.Content === "string" && typeof item.Title === "string") ? items : [];
  } catch { return []; }
};

export const withInlineHtmlBlock = (component: IContentComponent, propertyId: number, index: number, html: string): IContentComponent => {
  const property = component.BaseComponent.Properties.find(item => item.Id === propertyId);
  const items = inlineHtmlBlocks(property?.PublishedValue ?? property?.DefaultValue ?? "");
  if (!items[index]) return component;
  return withInlineHtml(component, propertyId, JSON.stringify(items.map((item, i) => i === index ? { ...item, Content: html } : item)));
};

export interface IInlineHtmlRegion {
  property: NonNullable<ReturnType<typeof inlineHtmlProperty>>;
  selector?: string;
  index: number;
  html: string;
  plainText?: boolean;
  itemField?: string;
  itemLabel?: string;
}

// Renderer classes already used by editorSiteFrame. Keep field mappings here,
// rather than separate preview/editor implementations for each component.
const inlineFields: Record<string, [string, string][]> = {
  banner: [["Text", ".cms-banner-text"]],
  card: [["BodyText", ".cms-card-text"], ["Title", ".cms-card-title"]],
  titleandtext: [["BodyText", ".cms-titletext-body"], ["Title", ".cms-titletext-title"]],
  image: [["Caption", ".cms-image-caption"]],
  contactprofile: [["InfoText", ".cms-profile-info-container"]],
  newsarticle: [["Title", ".cms-article-title"], ["Intro", ".cms-article-intro"], ["Body", ".cms-article-body-container"]],
  newssummary: [["Title", ".cms-newssummary-title"]],
  subscribe: [["ContentBlock", ".cms-subscribe-contentcontainer"]],
  accordion: [["Title", ".cms-accordion-title"]]
};

export const inlineHtmlRegions = (component?: IContentComponent): IInlineHtmlRegion[] => {
  if (!component) return [];
  const property = inlineHtmlProperty(component);
  if (property) {
    const html = property.PublishedValue ?? property.DefaultValue ?? "";
    return property.Type.Name.toLowerCase() === "htmlitems"
      ? inlineHtmlBlocks(html).map((item, index) => ({ property, html: item.Content, selector: ".cms-htmlblock-content-container", index, itemField: "Content", itemLabel: item.Title }))
      : [{ property, html, index: 0 }];
  }
  const collection = component.BaseComponent.Properties.find(property => property.UserEditable && !property.External
    && ["accordionitems", "tabitems", "carousel"].includes(property.Type.Name.toLowerCase()));
  const itemFields: Record<string, [string, string][]> = {
    accordionitems: [["Title", ".accordion-button"], ["Content", ".accordion-body"]],
    tabitems: [["Title", ".cms-tabs-container .nav-link"], ["Content", ".cms-tabs-container .tab-pane"]],
    carousel: [["Title", ".cms-carousel-caption-title"], ["Text", ".cms-carousel-caption-text"]]
  };
  let collectionRegions: IInlineHtmlRegion[] = [];
  if (collection) {
    try {
      const items = JSON.parse(collection.PublishedValue ?? collection.DefaultValue ?? "[]");
      if (Array.isArray(items)) collectionRegions = items.flatMap((item, index) =>
        itemFields[collection.Type.Name.toLowerCase()].map(([itemField, selector]) => ({
          property: collection, index, itemField, selector, html: item[itemField] ?? "", itemLabel: item.Title ?? `Item ${index + 1}`
        })));
    } catch { /* Malformed collections stay in their existing property editor. */ }
  }
  return [...collectionRegions, ...(inlineFields[component.BaseComponent.Name.trim().toLowerCase()] ?? []).flatMap(([name, selector]) => {
    const property = component.BaseComponent.Properties.find(value => value.PropertyName.toLowerCase() === name.toLowerCase());
    const type = property?.Type.Name.toLowerCase();
    return property?.UserEditable && !property.External && (type === "bodytext" || type === "string")
      ? [{ property, selector, index: 0, html: property.PublishedValue ?? property.DefaultValue ?? "", plainText: type === "string" }] : [];
  })];
};

export const withInlineRegion = (component: IContentComponent, region: IInlineHtmlRegion, html: string): IContentComponent => {
  if (!region.itemField) return withInlineHtml(component, region.property.Id, html);
  try {
    const items = JSON.parse(region.property.PublishedValue ?? region.property.DefaultValue ?? "[]");
    if (!Array.isArray(items) || !items[region.index]) return component;
    return withInlineHtml(component, region.property.Id, JSON.stringify(items.map((item, index) =>
      index === region.index ? { ...item, [region.itemField!]: html } : item)));
  } catch { return component; }
};

export const normalizeInlineContainers = (doc: Document, regions: IInlineHtmlRegion[]): void => {
  // Some renderer templates put rich HTML inside a paragraph (Card body).
  // The parser moves block children out of that paragraph. Reunite the authored
  // fragment in a valid container before adding an editor, avoiding duplicates.
  regions.forEach(region => {
    if (!region.selector || region.plainText) return;
    const target = doc.querySelectorAll(region.selector)[region.index];
    if (target?.tagName !== 'P') return;
    const authored = new DOMParser().parseFromString(region.html, 'text/html');
    let removed = false;
    for (const child of Array.from(authored.body.children)) {
      const sibling = target.nextElementSibling;
      if (sibling?.tagName === child.tagName && sibling.textContent === child.textContent) { sibling.remove(); removed = true; }
    }
    if (removed && target.nextElementSibling?.tagName === 'P' && !target.nextElementSibling.textContent?.trim()) target.nextElementSibling.remove();
    const container = doc.createElement('div');
    for (const attribute of Array.from(target.attributes)) container.setAttribute(attribute.name, attribute.value);
    container.innerHTML = authored.body.innerHTML;
    target.replaceWith(container);
  });
};
